import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, KeyRound, Info, Lock, Check, FlaskConical, CircleCheck, CircleX, Camera, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Method = 'authorization' | 'credentials';
type AuthStatus = 'none' | 'pending' | 'granted' | 'revoked';

interface SetAccess {
  method: Method | null;
  authStatus: AuthStatus;
  authGrantedAt: string | null;
  authorizedParty: string | null;
  credConfigured: boolean;
  credConfiguredAt: string | null;
  accessAuthorized?: boolean;
  accessAuthorizedAt?: string | null;
  notes: string | null;
}
interface AccessResp {
  access: SetAccess;
  credentialsCustodyAvailable: boolean;
}

interface ProbeReport {
  ok: boolean;
  authSucceeded: boolean | null;
  detected: {
    finalUrl: string;
    pageTitle: string | null;
    isLikelySpa: boolean;
    hasRecaptcha: boolean;
    hasViewState: boolean;
    loginFormAction: string | null;
    formFields: string[];
  };
  steps: Array<{ step: string; url: string; status: number; note?: string }>;
  extracted?: Record<string, unknown>;
  error?: string;
}

interface CaptureReport {
  ok: boolean;
  authSucceeded: boolean | null;
  finalUrl: string;
  pageTitle: string | null;
  screenshotBase64: string | null;
  links: Array<{ text: string; href: string }>;
  htmlPreview: string | null;
  steps: Array<{ step: string; url: string; note?: string }>;
  error?: string;
}

const AUTH_STATUS_LABEL: Record<AuthStatus, string> = {
  none: 'Sin configurar',
  pending: 'Pendiente',
  granted: 'Otorgada',
  revoked: 'Revocada',
};
function authVariant(s: AuthStatus): 'secondary' | 'warning' | 'success' | 'destructive' {
  if (s === 'granted') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'revoked') return 'destructive';
  return 'secondary';
}

/**
 * Card de acceso a la SET. `endpoint` es la base del recurso:
 *  - admin: `/api/admin/tenants/:id/set-access`
 *  - tenant: `/api/app/set-access`
 * El probe usa `${endpoint}/probe`.
 *
 * `showProbe` controla si se muestra el botón "Probar conexión" + diagnóstico.
 * En el panel tenant va en `false`: el contribuyente solo carga y guarda sus
 * accesos; el diagnóstico de login lo corremos nosotros desde el panel admin.
 */
export function SetAccessCard({
  endpoint,
  showProbe = true,
}: {
  endpoint: string;
  showProbe?: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['set-access', endpoint],
    queryFn: () => api<AccessResp>(endpoint),
  });

  const [method, setMethod] = useState<Method>('authorization');
  const [authStatus, setAuthStatus] = useState<AuthStatus>('none');
  const [authorizedParty, setAuthorizedParty] = useState('');
  const [notes, setNotes] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (!data) return;
    setMethod(data.access.method ?? 'authorization');
    setAuthStatus(data.access.authStatus);
    setAuthorizedParty(data.access.authorizedParty ?? '');
    setNotes(data.access.notes ?? '');
    setAuthorized(!!data.access.accessAuthorized);
  }, [data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['set-access', endpoint] });
      setUsername('');
      setPassword('');
      toast.success('Acceso a la SET actualizado');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  const [forceExpanded, setForceExpanded] = useState<boolean | null>(null);
  // Copia al portapapeles la credencial guardada (login manual si el scraping
  // falla). Se desencripta server-side, se copia y NO se muestra en pantalla.
  const [copying, setCopying] = useState<'username' | 'password' | null>(null);
  const copyCredential = async (field: 'username' | 'password') => {
    setCopying(field);
    try {
      const r = await api<Record<string, string>>(`${endpoint}/reveal?field=${field}`, { method: 'POST' });
      await navigator.clipboard.writeText(r[field] ?? '');
      toast.success(`${field === 'username' ? 'Usuario' : 'Clave'} copiado al portapapeles`);
    } catch {
      toast.error('No se pudo copiar la credencial');
    } finally {
      setCopying(null);
    }
  };
  const [report, setReport] = useState<ProbeReport | null>(null);
  const probe = useMutation({
    mutationFn: () => api<ProbeReport>(`${endpoint}/probe`, { method: 'POST' }),
    onSuccess: (r) => setReport(r),
    onError: () => toast.error('No se pudo correr el diagnóstico'),
  });

  const [capture, setCapture] = useState<CaptureReport | null>(null);
  const captureMut = useMutation({
    mutationFn: () => api<CaptureReport>(`${endpoint}/capture`, { method: 'POST' }),
    onSuccess: (r) => setCapture(r),
    onError: () => toast.error('No se pudo correr la captura'),
  });

  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Acceso a la SET (Marangatu)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Cargando…</CardContent>
      </Card>
    );
  }

  const { access, credentialsCustodyAvailable } = data;

  // El acceso se configura UNA vez y casi no se vuelve a tocar. Cuando ya está
  // configurado, colapsamos el card a una línea para no ocupar media pantalla en
  // el medio de la info. `expanded` override manual; default = !configurado.
  const isConfigured =
    access.method === 'credentials' ? access.credConfigured : access.authStatus === 'granted';
  const expanded = forceExpanded ?? !isConfigured;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Acceso a la SET (Marangatu)</CardTitle>
          <div className="flex items-center gap-2">
            {access.method === 'credentials' ? (
              <Badge variant={access.credConfigured ? 'success' : 'secondary'}>
                {access.credConfigured ? 'Credenciales configuradas' : 'Sin credenciales'}
              </Badge>
            ) : (
              <Badge variant={authVariant(access.authStatus)}>
                {AUTH_STATUS_LABEL[access.authStatus]}
              </Badge>
            )}
            <Button size="sm" variant="ghost" onClick={() => setForceExpanded(!expanded)}>
              {expanded ? <ChevronUp /> : <ChevronDown />}
              {expanded ? 'Ocultar' : 'Gestionar'}
            </Button>
          </div>
        </div>
        {expanded && (
          <CardDescription>
            Cómo presentamos las declaraciones de este contribuyente ante la SET.
          </CardDescription>
        )}
      </CardHeader>

      {!expanded ? null : (
      <CardContent className="space-y-5">
        <div className="grid gap-1.5">
          <Label>Método</Label>
          <Select value={method} onValueChange={(v) => setMethod(v as Method)}>
            <SelectTrigger className="max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="authorization">Autorización de terceros (recomendado)</SelectItem>
              <SelectItem value="credentials">Credenciales de Marangatu</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {method === 'authorization' && (
          <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success" />
              El contribuyente autoriza al RUC de Actuo como tercero en Marangatu. Presentamos con
              nuestro propio acceso — sin guardar su clave.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="sa-party">RUC / nombre autorizado (Actuo)</Label>
                <Input
                  id="sa-party"
                  value={authorizedParty}
                  onChange={(e) => setAuthorizedParty(e.target.value)}
                  placeholder="80012345-6 · Actuo"
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Estado de la autorización</Label>
                <Select value={authStatus} onValueChange={(v) => setAuthStatus(v as AuthStatus)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin configurar</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="granted">Otorgada</SelectItem>
                    <SelectItem value="revoked">Revocada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              size="sm"
              disabled={save.isPending}
              onClick={() => save.mutate({ method: 'authorization', authStatus, authorizedParty })}
            >
              Guardar autorización
            </Button>
          </div>
        )}

        {method === 'credentials' && (
          <div className="space-y-4 rounded-md border border-border bg-muted/30 p-4">
            {!credentialsCustodyAvailable ? (
              <p className="flex items-start gap-2 text-sm text-warning-foreground">
                <Info className="mt-0.5 size-4 shrink-0 text-warning" />
                La custodia de credenciales está deshabilitada (falta configurar
                <code className="mx-1 rounded bg-muted px-1 font-mono text-xs">SET_CREDENTIALS_KEY</code>
                en el servidor). Usá autorización de terceros.
              </p>
            ) : (
              <>
                <p className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Lock className="mt-0.5 size-4 shrink-0" />
                  Se guardan <strong>cifradas</strong> (AES-256-GCM). No se muestran después de
                  guardar — solo se pueden reemplazar o borrar.
                </p>
                {access.credConfigured && (
                  <div className="space-y-2">
                    <p className="flex items-center gap-1.5 text-sm text-success">
                      <Check className="size-4" />
                      Credenciales configuradas
                      {access.credConfiguredAt
                        ? ` (${new Date(access.credConfiguredAt).toLocaleDateString('es-PY')})`
                        : ''}
                    </p>
                    {/* Login MANUAL (fallback si el scraping se rompe): copia al
                        portapapeles sin mostrar. El acceso queda en el audit log. */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-muted-foreground">Para entrar a mano:</span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={copying !== null}
                        onClick={() => void copyCredential('username')}
                      >
                        <Copy /> {copying === 'username' ? 'Copiando…' : 'Copiar usuario'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={copying !== null}
                        onClick={() => void copyCredential('password')}
                      >
                        <Copy /> {copying === 'password' ? 'Copiando…' : 'Copiar clave'}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="sa-user">Usuario Marangatu</Label>
                    <Input
                      id="sa-user"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="sa-pass">Clave</Label>
                    <Input
                      id="sa-pass"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                {/* Autorización del titular. Tenant: checkbox requerido. Admin: estado. */}
                {!showProbe && (
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
                    <Checkbox
                      checked={authorized}
                      onCheckedChange={(v) => setAuthorized(v === true)}
                      className="mt-0.5"
                    />
                    <span>
                      Autorizo a Actuo a ingresar a mi cuenta de Marangatu para obtener los datos
                      necesarios para gestionar mis obligaciones fiscales (perfil, saldo a favor,
                      presentaciones). Puedo revocar este acceso borrando mis credenciales.
                    </span>
                  </label>
                )}
                {showProbe &&
                  (access.accessAuthorized ? (
                    <p className="flex items-center gap-1.5 text-sm text-success">
                      <Check className="size-4" />
                      El titular autorizó el acceso
                      {access.accessAuthorizedAt
                        ? ` (${new Date(access.accessAuthorizedAt).toLocaleDateString('es-PY')})`
                        : ''}
                    </p>
                  ) : (
                    <p className="flex items-center gap-1.5 text-sm text-warning-foreground">
                      <Info className="size-4 text-warning" />
                      El titular aún no autorizó el acceso — no se puede sincronizar.
                    </p>
                  ))}

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={
                      save.isPending ||
                      !username.trim() ||
                      !password.trim() ||
                      (!showProbe && !authorized)
                    }
                    onClick={() =>
                      save.mutate({
                        method: 'credentials',
                        credentials: { username: username.trim(), password },
                        ...(showProbe ? {} : { accessAuthorized: authorized }),
                      })
                    }
                  >
                    <KeyRound />
                    {access.credConfigured ? 'Reemplazar credenciales' : 'Guardar credenciales'}
                  </Button>
                  {access.credConfigured && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      disabled={save.isPending}
                      onClick={() => save.mutate({ clearCredentials: true })}
                    >
                      Borrar credenciales
                    </Button>
                  )}
                  {showProbe && access.credConfigured && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={probe.isPending}
                      onClick={() => probe.mutate()}
                    >
                      <FlaskConical />
                      {probe.isPending ? 'Probando…' : 'Probar conexión'}
                    </Button>
                  )}
                  {showProbe && access.credConfigured && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={captureMut.isPending}
                      onClick={() => captureMut.mutate()}
                      title="Login headless + captura del DOM/screenshot post-login (debug)"
                    >
                      <Camera />
                      {captureMut.isPending ? 'Capturando…' : 'Capturar perfil (debug)'}
                    </Button>
                  )}
                </div>

                {showProbe && report && <ProbeResult report={report} />}
                {showProbe && capture && <CaptureResult report={capture} />}
              </>
            )}
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="sa-notes">Notas internas</Label>
          <Textarea
            id="sa-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if ((notes ?? '') !== (access.notes ?? '')) save.mutate({ notes });
            }}
            placeholder="Ej: autorización solicitada el 12/05, pendiente confirmación del cliente."
          />
        </div>
      </CardContent>
      )}
    </Card>
  );
}

function ProbeResult({ report }: { report: ProbeReport }) {
  const auth = report.authSucceeded;
  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-3 text-sm">
      <div className="flex items-center gap-2 font-medium">
        {auth === true ? (
          <>
            <CircleCheck className="size-4 text-success" /> Login OK — pudimos entrar
          </>
        ) : auth === false ? (
          <>
            <CircleX className="size-4 text-destructive" /> Login falló
          </>
        ) : (
          <>
            <Info className="size-4 text-warning" /> Diagnóstico (login no concluyente)
          </>
        )}
      </div>

      {report.error && (
        <p className="text-xs text-muted-foreground">
          Resultado: <code className="rounded bg-muted px-1 font-mono">{report.error}</code>
        </p>
      )}

      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {report.detected.pageTitle && <li>Título: {report.detected.pageTitle}</li>}
        <li>URL final: <span className="font-mono break-all">{report.detected.finalUrl}</span></li>
        <li>reCAPTCHA: {report.detected.hasRecaptcha ? 'sí' : 'no'}</li>
        <li>SPA (JS): {report.detected.isLikelySpa ? 'sí' : 'no'}</li>
        <li>JSF ViewState: {report.detected.hasViewState ? 'sí' : 'no'}</li>
        {report.detected.formFields.length > 0 && (
          <li>Campos del login: <span className="font-mono">{report.detected.formFields.join(', ')}</span></li>
        )}
      </ul>

      {typeof report.extracted?.preview === 'string' && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Vista previa de la respuesta</summary>
          <p className="mt-1 max-h-40 overflow-auto rounded bg-muted p-2 font-mono">
            {report.extracted.preview as string}
          </p>
        </details>
      )}

      <p className="text-[11px] text-muted-foreground">
        Diagnóstico exploratorio. Si pide captcha o es un SPA, el login por HTTP no alcanza y vamos a
        necesitar un browser headless — ese es el dato que estamos buscando.
      </p>
    </div>
  );
}

function CaptureResult({ report }: { report: CaptureReport }) {
  const auth = report.authSucceeded;
  return (
    <div className="space-y-3 rounded-md border border-border bg-background p-3 text-sm">
      <div className="flex items-center gap-2 font-medium">
        {auth === true ? (
          <>
            <CircleCheck className="size-4 text-success" /> Captura post-login OK
          </>
        ) : auth === false ? (
          <>
            <CircleX className="size-4 text-destructive" /> Login falló (capturado igual)
          </>
        ) : (
          <>
            <Info className="size-4 text-warning" /> Captura (login no concluyente)
          </>
        )}
      </div>

      {report.error && (
        <p className="text-xs text-muted-foreground">
          Resultado: <code className="rounded bg-muted px-1 font-mono">{report.error}</code>
        </p>
      )}

      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {report.pageTitle && <li>Título: {report.pageTitle}</li>}
        <li>URL final: <span className="font-mono break-all">{report.finalUrl}</span></li>
      </ul>

      {report.screenshotBase64 && (
        <div className="overflow-hidden rounded border border-border">
          <img
            src={`data:image/png;base64,${report.screenshotBase64}`}
            alt="Captura post-login Marangatu"
            className="max-h-96 w-full object-contain object-top"
          />
        </div>
      )}

      {report.links.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Links de navegación ({report.links.length}) — para ubicar el perfil
          </summary>
          <ul className="mt-1 max-h-48 space-y-0.5 overflow-auto rounded bg-muted p-2">
            {report.links.map((l, i) => (
              <li key={i} className="truncate">
                <span className="font-medium">{l.text}</span>{' '}
                <span className="font-mono text-muted-foreground">{l.href}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {report.htmlPreview && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">HTML (preview, redactado)</summary>
          <pre className="mt-1 max-h-60 overflow-auto rounded bg-muted p-2 font-mono text-[10px] whitespace-pre-wrap">
            {report.htmlPreview}
          </pre>
        </details>
      )}

      <p className="text-[11px] text-muted-foreground">
        Fase A: esta captura nos da el DOM real post-login para escribir los selectores del perfil
        (actividad económica, domicilio, etc.) en la Fase B — sin adivinar.
      </p>
    </div>
  );
}
